import { useCallback, useEffect, useState } from 'react';
import { notesApiService } from '../services/notesApi';
import { TicketTranscriptResponse } from '../types/ticketTranscript';

// Transcripts belong to closed tickets and never change, so successful fetches are
// cached for the session. The upstream service allows only 10 requests/min per
// token — this cache plus in-flight dedupe is the rate-limit protection.
const transcriptCache = new Map<number, TicketTranscriptResponse>();
const pendingFetches = new Map<number, Promise<TicketTranscriptResponse>>();

/**
 * Fetches a ticket transcript with session caching. Errors are not cached so
 * `retry()` can refire the request.
 */
export function useTicketTranscript(ticketId: number): {
  data: TicketTranscriptResponse | null;
  error: Error | null;
  loading: boolean;
  retry: () => void;
} {
  const cached = transcriptCache.get(ticketId) ?? null;
  const [data, setData] = useState<TicketTranscriptResponse | null>(cached);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(!cached);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const hit = transcriptCache.get(ticketId);
    if (hit) {
      setData(hit);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setData(null);
    setError(null);
    setLoading(true);

    let fetchPromise = pendingFetches.get(ticketId);
    if (!fetchPromise) {
      fetchPromise = notesApiService
        .getTicketTranscript(ticketId)
        .then(response => {
          transcriptCache.set(ticketId, response);
          return response;
        })
        .finally(() => {
          pendingFetches.delete(ticketId);
        });
      pendingFetches.set(ticketId, fetchPromise);
    }

    fetchPromise
      .then(response => {
        if (cancelled) return;
        setData(response);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticketId, attempt]);

  const retry = useCallback(() => setAttempt(a => a + 1), []);

  return { data, error, loading, retry };
}
